import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  Save, 
  X, 
  Plus, 
  Minus, 
  MapPin, 
  Phone, 
  Clock, 
  Truck,
  User,
  Calendar as CalendarIcon,
  Navigation
} from 'lucide-react';
import { masterDataService } from '../services/apiService';
import { 
  CUSTOMER_FIELDS,
  DRIVER_FIELDS,
  TRUCK_FIELDS,
  PAYLOAD_FIELDS,
  SHIPPING_ADDRESS_FIELDS,
  TRANSPORT_TYPES,
  TRANSPORT_TYPE_TEXT,
  SHIPMENT_STATUS
} from '../constants';
import SearchableSelect from './SearchableSelect';

const CreateShipment = ({ onBack, onSave, userData }) => {
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [payloads, setPayloads] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Basic info
    ngaynhap: getCurrentDateISO(),
    ngaynhan: getCurrentDateISO(),
    loaivantai: TRANSPORT_TYPES.SINGLE_POINT, // Default to single point delivery
    
    // Vehicle and driver
    idTrongTai: '',
    idXe: '',
    idLaiXe: '',
    
    idKhachHang: '',
    // Pickup info
    idKhachHangNhan: '',
    idDiaChiLayHang: '',
    diaChiLayHang: '',
    lienhenhan: '',
    linkmapnhan: '',
    gionhan1: '',
    phutnhan1: '',
    
    // Delivery info (up to 4 points)
    deliveryPoints: [
      {
        idKhachHangGiao: '',
        idDiaChiGiaoHang: '',
        diaChiGiaoHang: '',
        lienhegiao: '',
        linkmapgiao: '',
        ngaygiao: getCurrentDateISO(),
        giogiao: '',
        phutgiao: ''
      }
    ],
    
    // Pricing and notes
    giatri: '',
    ghichu: '',
    trangthai: SHIPMENT_STATUS.WAITING_CONFIRMATION
  });

  const customerOptions = customers.map(customer => ({
    id: customer[CUSTOMER_FIELDS.ID],
    name: customer[CUSTOMER_FIELDS.NAME],
    code: customer[CUSTOMER_FIELDS.CODE],
    address: customer[CUSTOMER_FIELDS.ADDRESS],
    contact: customer[CUSTOMER_FIELDS.CONTACT]
  }));

  const driverOptions = drivers.map(driver => ({
    id: driver[DRIVER_FIELDS.ID],
    name: driver[DRIVER_FIELDS.NAME],
    phone: driver[DRIVER_FIELDS.PHONE],
    address: driver[DRIVER_FIELDS.ADDRESS]
  }));

  const truckOptions = trucks.map(truck => ({
    id: truck[TRUCK_FIELDS.ID],
    name: truck[TRUCK_FIELDS.LICENSE_PLATE],
    licensePlate: truck[TRUCK_FIELDS.LICENSE_PLATE],
    payload: truck[TRUCK_FIELDS.PAYLOAD]
  }));

  const payloadOptions = payloads.map(payload => ({
    id: payload[PAYLOAD_FIELDS.ID],
    name: payload[PAYLOAD_FIELDS.WEIGHT],
    weight: payload[PAYLOAD_FIELDS.WEIGHT]
  }));

  function getCurrentDate() {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function getCurrentDateISO() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  function formatDateToAPI(isoDate) {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function formatDateFromAPI(apiDate) {
    if (!apiDate) return '';
    const parts = apiDate.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
  }

  // Helper function to format time from separate hour/minute to HH:MM format
  function formatTimeFromHourMinute(hour, minute) {
    if (!hour && !minute) return '';
    const h = hour ? String(hour).padStart(2, '0') : '00';
    const m = minute ? String(minute).padStart(2, '0') : '00';
    return `${h}:${m}`;
  }

  // Helper function to parse time from HH:MM format to separate hour/minute
  function parseTimeToHourMinute(timeString) {
    if (!timeString) return { hour: '', minute: '' };
    const [hour, minute] = timeString.split(':');
    return {
      hour: hour ? parseInt(hour, 10).toString() : '',
      minute: minute ? parseInt(minute, 10).toString() : ''
    };
  }

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setLoading(true);
    try {
      const [customersResult, driversResult, trucksResult, payloadsResult] = await Promise.all([
        masterDataService.getCustomers(),
        masterDataService.getDrivers(),
        masterDataService.getTrucks(),
        masterDataService.getPayload(),
        
      ]);

      if (customersResult.success) setCustomers(customersResult.data || []);
      if (driversResult.success) setDrivers(driversResult.data || []);
      if (trucksResult.success) setTrucks(trucksResult.data || []);
      if (payloadsResult.success) setPayloads(payloadsResult.data || []);
      
    } catch (err) {
      setError('Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeliveryPointChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      deliveryPoints: prev.deliveryPoints.map((point, i) => 
        i === index ? { ...point, [field]: value } : point
      )
    }));
  };

  // Handle pickup time change
  const handlePickupTimeChange = (timeString) => {
    const { hour, minute } = parseTimeToHourMinute(timeString);
    setFormData(prev => ({
      ...prev,
      gionhan1: hour,
      phutnhan1: minute
    }));
  };

  // Handle delivery time change
  const handleDeliveryTimeChange = (index, timeString) => {
    const { hour, minute } = parseTimeToHourMinute(timeString);
    setFormData(prev => ({
      ...prev,
      deliveryPoints: prev.deliveryPoints.map((point, i) => 
        i === index ? { ...point, giogiao: hour, phutgiao: minute } : point
      )
    }));
  };

  const addDeliveryPoint = () => {
    if (formData.deliveryPoints.length < 4) {
      setFormData(prev => ({
        ...prev,
        deliveryPoints: [
          ...prev.deliveryPoints,
          {
            idKhachHangGiao: '',
            idDiaChiGiaoHang: '',
            diaChiGiaoHang: '',
            lienhegiao: '',
            linkmapgiao: '',
            ngaygiao: getCurrentDateISO(),
            giogiao: '',
            phutgiao: ''
          }
        ]
      }));
    }
  };

  const removeDeliveryPoint = (index) => {
    if (formData.deliveryPoints.length > 1) {
      setFormData(prev => ({
        ...prev,
        deliveryPoints: prev.deliveryPoints.filter((_, i) => i !== index)
      }));
    }
  };

  const handleCustomerChange = (field, customerId, isPickup = false) => {
    const customer = customers.find(c => c[CUSTOMER_FIELDS.ID] === parseInt(customerId));
    
    if (isPickup) {
      handleFieldChange('idKhachHangNhan', customerId);
      if (customer) {
        handleFieldChange('diaChiLayHang', customer[CUSTOMER_FIELDS.ADDRESS]);
        handleFieldChange('lienhenhan', customer[CUSTOMER_FIELDS.CONTACT]);
      }
    } else {
      // Handle delivery customer change
      const [pointField, pointIndex] = field.split('_');
      const index = parseInt(pointIndex);
      
      handleDeliveryPointChange(index, 'idKhachHangGiao', customerId);
      if (customer) {
        handleDeliveryPointChange(index, 'diaChiGiaoHang', customer[CUSTOMER_FIELDS.DELIVERY_ADDRESS] || customer[CUSTOMER_FIELDS.ADDRESS]);
        handleDeliveryPointChange(index, 'lienhegiao', customer[CUSTOMER_FIELDS.CONTACT]);
      }
    }
  };

  const validateForm = () => {
    const errors = [];
    
    // Required fields validation
    if (!formData.idKhachHang) errors.push('Chọn khách hàng');
    if (!formData.idKhachHangNhan) errors.push('Chọn khách hàng lấy hàng');
    if (!formData.diaChiLayHang) errors.push('Nhập địa chỉ lấy hàng');
    if (!formData.idLaiXe) errors.push('Chọn lái xe');
    if (!formData.idXe) errors.push('Chọn xe');
    if (!formData.loaivantai) errors.push('Chọn loại vận tải');
    
    // Validate delivery points
    formData.deliveryPoints.forEach((point, index) => {
      if (!point.idKhachHangGiao) errors.push(`Chọn khách hàng giao hàng điểm ${index + 1}`);
      if (!point.diaChiGiaoHang) errors.push(`Nhập địa chỉ giao hàng điểm ${index + 1}`);
    });
    
    return errors;
  };

  // Update the handleSave function in CreateShipment component
  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      alert('Vui lòng điền đầy đủ thông tin:\n' + errors.join('\n'));
      return;
    }

    setSaving(true);
    try {
      // Prepare data for API - convert dates to API format
      const saveData = {
        idChuyenXe: '0', // Always 0 for new shipment
        ngaynhap: formatDateToAPI(formData.ngaynhap) || '',
        ngaynhan: formatDateToAPI(formData.ngaynhan) || '',
        
        // Transport type - API needs this value for both loaivantai and loaixetai_fk
        loaivantai: formData.loaivantai || TRANSPORT_TYPES.SINGLE_POINT,
        loaixetai_fk : 0,
        idTrongTai: formData.idTrongTai || '',
        idXe: formData.idXe || '',
        idLaiXe: formData.idLaiXe || '',
        
        // Required fields
        idKhachHang: formData.idKhachHang || '', // REQUIRED for new shipment
        idKhachHangNhan: formData.idKhachHangNhan || '',
        
        idDiaChiLayHang: formData.idDiaChiLayHang || '',
        diaChiLayHang: formData.diaChiLayHang || '',
        lienhenhan: formData.lienhenhan || '',
        linkmapnhan: formData.linkmapnhan || '',
        gionhan1: formData.gionhan1 || '',
        phutnhan1: formData.phutnhan1 || '',
        giatri: formData.giatri || '',
        ghichu: formData.ghichu || '',
        trangthai: formData.trangthai || SHIPMENT_STATUS.WAITING_CONFIRMATION,
        userName: userData.UserName || userData.userName || ''
      };

      // Debug log to check the data being sent
      console.log('Form data:', formData);
      console.log('Save data being sent to API:', saveData);

      // Add delivery points - convert dates to API format
      formData.deliveryPoints.forEach((point, index) => {
        const pointNum = index + 1;
        saveData[`ngaygiao${pointNum}`] = formatDateToAPI(point.ngaygiao) || '';
        saveData[`idKhachHangGiao${pointNum}`] = point.idKhachHangGiao || '';
        saveData[`idDiaChiGiaoHang${pointNum}`] = point.idDiaChiGiaoHang || '';
        saveData[`diaChiGiaoHang${pointNum}`] = point.diaChiGiaoHang || '';
        saveData[`lienhegiao${pointNum}`] = point.lienhegiao || '';
        saveData[`linkmapgiao${pointNum}`] = point.linkmapgiao || '';
        saveData[`giogiao${pointNum}`] = point.giogiao || '';
        saveData[`phutgiao${pointNum}`] = point.phutgiao || '';
      });

      // Validation before sending - ensure required fields are not empty
      const requiredFields = {
        'idKhachHang': 'Khách hàng',
        'idKhachHangNhan': 'Khách hàng nhận',
        'diaChiLayHang': 'Địa chỉ lấy hàng',
        'idLaiXe': 'Lái xe',
        'idXe': 'Xe',
        'loaivantai': 'Loại vận tải'
      };

      const missingFields = [];
      Object.keys(requiredFields).forEach(field => {
        if (!saveData[field] || saveData[field] === '') {
          missingFields.push(requiredFields[field]);
        }
      });

      if (missingFields.length > 0) {
        alert('Các trường bắt buộc còn thiếu:\n' + missingFields.join('\n'));
        return;
      }

      console.log('Final data sent to API:', saveData);

      const result = await masterDataService.saveShipment(saveData);
      
      if (result.success) {
        alert('Tạo chuyến xe thành công');
        if (onSave) onSave(saveData);
        onBack();
      } else {
        alert(result.message || 'Tạo chuyến xe thất bại');
      }
    } catch (error) {
      console.error('Error creating shipment:', error);
      alert('Có lỗi xảy ra khi tạo chuyến xe');
    } finally {
      setSaving(false);
    }
  };

  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={onBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen pb-20">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button 
                onClick={onBack} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold">Tạo chuyến xe mới</h2>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4">Thông tin cơ bản</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loại vận tải
              </label>
              <select
                value={formData.loaivantai}
                onChange={(e) => handleFieldChange('loaivantai', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={TRANSPORT_TYPES.SINGLE_POINT}>{TRANSPORT_TYPE_TEXT[TRANSPORT_TYPES.SINGLE_POINT]}</option>
                <option value={TRANSPORT_TYPES.MULTIPLE_POINTS}>{TRANSPORT_TYPE_TEXT[TRANSPORT_TYPES.MULTIPLE_POINTS]}</option>
                <option value={TRANSPORT_TYPES.BY_DAY}>{TRANSPORT_TYPE_TEXT[TRANSPORT_TYPES.BY_DAY]}</option>
              </select>
            </div>
           
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Khách hàng <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={formData.idKhachHang}
                onChange={(value) => handleFieldChange('idKhachHang', value)}
                options={customerOptions}
                placeholder="Chọn khách hàng"
                displayField="name"
                valueField="id"
                secondaryField="code"
                title="Chọn khách hàng"
                searchPlaceholder="Tìm theo tên hoặc mã khách hàng..."
                required={true}
              />
            </div>
          </div>
        </div>

        {/* Vehicle and Driver Information */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4">
            <Truck className="w-5 h-5 inline mr-2" />
            Thông tin xe và lái xe
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tải trọng <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={formData.idTrongTai}
                onChange={(value) => handleFieldChange('idTrongTai', value)}
                options={payloadOptions}
                placeholder="Chọn tải trọng"
                displayField="weight"
                valueField="id"
                title="Chọn tải trọng"
                searchPlaceholder="Tìm theo tải trọng..."
                required={true}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Xe <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={formData.idXe}
                onChange={(value) => handleFieldChange('idXe', value)}
                options={truckOptions}
                placeholder="Chọn xe"
                displayField="licensePlate"
                valueField="id"
                secondaryField="payload"
                title="Chọn xe"
                searchPlaceholder="Tìm theo biển số xe..."
                required={true}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Lái xe <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                value={formData.idLaiXe}
                onChange={(value) => handleFieldChange('idLaiXe', value)}
                options={driverOptions}
                placeholder="Chọn lái xe"
                displayField="name"
                valueField="id"
                secondaryField="phone"
                title="Chọn lái xe"
                searchPlaceholder="Tìm theo tên hoặc số điện thoại..."
                required={true}
              />
            </div>
          </div>
        </div>

        {/* Pickup Information */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4 text-blue-600">
            <MapPin className="w-5 h-5 inline mr-2" />
            Thông tin lấy hàng
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Điểm nhận <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  value={formData.idKhachHangNhan}
                  onChange={(value) => handleCustomerChange('pickup', value, true)}
                  options={customerOptions}
                  placeholder="Chọn điểm nhận"
                  displayField="name"
                  valueField="id"
                  secondaryField="address"
                  title="Chọn điểm nhận hàng"
                  searchPlaceholder="Tìm theo tên hoặc địa chỉ..."
                  required={true}
                />
              </div>
            </div>
            
            {/* Các trường khác giữ nguyên */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Địa chỉ nhận <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.diaChiLayHang}
                onChange={(e) => handleFieldChange('diaChiLayHang', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Nhập địa chỉ lấy hàng"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Liên hệ
                </label>
                <input
                  type="text"
                  value={formData.lienhenhan}
                  onChange={(e) => handleFieldChange('lienhenhan', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tên người liên hệ & số điện thoại"
                />
              </div>
              <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    <Navigation className="w-4 h-4 inline mr-1" />
    Link bản đồ nhận hàng
  </label>
  <input
    type="url"
    value={formData.linkmapnhan}
    onChange={(e) => handleFieldChange('linkmapnhan', e.target.value)}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    placeholder="https://maps.google.com/..."
  />
</div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarIcon className="w-4 h-4 inline mr-1" />
                  Ngày nhận hàng
                </label>
                <input
                  type="date"
                  value={formData.ngaynhan}
                  onChange={(e) => handleFieldChange('ngaynhan', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Thời gian lấy hàng
                </label>
                <input
                  type="time"
                  value={formatTimeFromHourMinute(formData.gionhan1, formData.phutnhan1)}
                  onChange={(e) => handlePickupTimeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="00:00"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Information */}
         <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-green-600">
              <Truck className="w-5 h-5 inline mr-2" />
              Thông tin giao hàng
            </h3>
            {formData.deliveryPoints.length < 4 && (
              <button
                onClick={addDeliveryPoint}
                className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 flex items-center gap-1 text-sm"
              >
                <Plus className="w-4 h-4" />
                Thêm điểm đến
              </button>
            )}
          </div>
          
          <div className="space-y-6">
            {formData.deliveryPoints.map((point, index) => (
              <div key={index} className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-green-600">Điểm giao {index + 1}</h4>
                  {formData.deliveryPoints.length > 1 && (
                    <button
                      onClick={() => removeDeliveryPoint(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Điểm đến <span className="text-red-500">*</span>
                      </label>
                      <SearchableSelect
                        value={point.idKhachHangGiao}
                        onChange={(value) => handleCustomerChange(`delivery_${index}`, value, false)}
                        options={customerOptions}
                        placeholder="Chọn điểm đến"
                        displayField="name"
                        valueField="id"
                        secondaryField="address"
                        title={`Chọn điểm giao hàng ${index + 1}`}
                        searchPlaceholder="Tìm theo tên hoặc địa chỉ..."
                        required={true}
                      />
                    </div>
                  </div>
                  
                  {/* Các trường khác của delivery point giữ nguyên */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Địa chỉ đến <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={point.diaChiGiaoHang}
                      onChange={(e) => handleDeliveryPointChange(index, 'diaChiGiaoHang', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nhập địa chỉ giao hàng"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Liên hệ
                      </label>
                      <input
                        type="text"
                        value={point.lienhegiao}
                        onChange={(e) => handleDeliveryPointChange(index, 'lienhegiao', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Tên & SĐT người nhận"
                      />
                    </div>
                    <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    <Navigation className="w-4 h-4 inline mr-1" />
    Link bản đồ giao hàng
  </label>
  <input
    type="url"
    value={point.linkmapgiao}
    onChange={(e) => handleDeliveryPointChange(index, 'linkmapgiao', e.target.value)}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    placeholder="https://maps.google.com/..."
  />
</div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <CalendarIcon className="w-4 h-4 inline mr-1" />
                        Ngày giao
                      </label>
                      <input
                        type="date"
                        value={point.ngaygiao}
                        onChange={(e) => handleDeliveryPointChange(index, 'ngaygiao', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Thời gian giao
                      </label>
                      <input
                        type="time"
                        value={formatTimeFromHourMinute(point.giogiao, point.phutgiao)}
                        onChange={(e) => handleDeliveryTimeChange(index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="00:00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing and Notes */}
         <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-4">Ghi chú</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ghi chú
              </label>
              <textarea
                value={formData.ghichu}
                onChange={(e) => handleFieldChange('ghichu', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Nhập ghi chú cho chuyến xe..."
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <div className="flex justify-end gap-3">
            <button
              onClick={onBack}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Đang tạo...' : 'Tạo chuyến xe'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateShipment;